import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, ShieldCheck, Calendar, Target, ArrowRight, Info, Share2, Facebook, Linkedin, Link2, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { Container, SectionLabel } from "../components/ui-kit";
import PageHero from "../components/PageHero";
import SupportCheckout from "../components/SupportCheckout";
import { toast } from "sonner";

export default function CampaignDetail() {
  const { slug } = useParams();
  const { t } = useI18n();
  const [c, setC] = useState(null);
  const [open, setOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/campaigns/${slug}`).then((r) => setC(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <Container className="py-32 text-center">
      <h1 className="font-display text-4xl font-bold text-emerald-900">Initiative not found</h1>
      <Link to="/programs" className="mt-6 inline-block rounded-full bg-emerald-900 px-6 py-3 text-sm font-bold text-white">Back to Programs</Link>
    </Container>
  );
  if (!c) return <div className="py-40 text-center text-slate-400">Loading…</div>;

  const canSupport = c.fundraising_enabled && c.payment_enabled;
  const pct = c.target_amount > 0 ? Math.min(100, Math.round(((c.raised_amount || 0) / c.target_amount) * 100)) : 0;

  return (
    <div>
      <PageHero
        label={c.category?.replace("-", " ")}
        title={c.title}
        subtitle={c.description}
        image={c.images?.[0] || c.image}
        crumbs={[{ label: "Programs", to: "/programs" }, { label: c.title }]}
      />

      <section className="bg-white">
        <Container className="grid gap-12 py-16 lg:grid-cols-[1.7fr_1fr]">
          <div>
            <img src={c.images?.[0] || c.image} alt={c.title} className="aspect-[16/9] w-full rounded-3xl object-cover hh-shadow" />

            <div className="mt-8 flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2 text-slate-600"><MapPin className="h-4 w-4 text-gold" /> {c.location}, {c.country}</div>
              {c.partner && <div className="flex items-center gap-2 text-slate-600"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Partner: {c.partner}</div>}
              <div className="flex items-center gap-2 text-slate-600"><Calendar className="h-4 w-4 text-gold" /> Started {c.start_date?.slice(0, 10)}</div>
            </div>

            <div className="mt-10 space-y-8">
              <div>
                <SectionLabel>Objective</SectionLabel>
                <h2 className="mt-3 font-display text-3xl font-bold text-emerald-900">Why it matters</h2>
                <p className="mt-3 leading-relaxed text-slate-600">{c.impact_goal || c.description}</p>
              </div>
              <div>
                <h3 className="font-display text-2xl font-bold text-emerald-900">What we are doing</h3>
                <p className="mt-3 leading-relaxed text-slate-600">{c.beneficiary_information || "Coordinated with verified implementation partners to deliver measurable outcomes with transparency and dignity."}</p>
              </div>

              <div className="rounded-2xl border border-emerald-900/10 bg-sand p-6">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900"><Info className="h-4 w-4 text-gold" /> Compliance & Transparency</div>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  This initiative is coordinated with a {c.partner ? "verified partner organisation" : "responsible partner"}. Contributions, where enabled, are recorded with a unique receipt number and can be independently verified. Tax treatment may vary by jurisdiction — please consult your local tax adviser.
                </p>
              </div>
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-3xl border border-emerald-900/10 bg-white p-7 hh-shadow">
              <div className="text-xs font-semibold uppercase tracking-wider text-gold">{canSupport ? "Support this initiative" : "CSR Program"}</div>
              {canSupport && c.target_amount > 0 && (
                <div className="mt-4">
                  <div className="flex items-end justify-between">
                    <span className="font-display text-3xl font-bold text-emerald-900">{c.currency} {(c.raised_amount || 0).toLocaleString()}</span>
                    <span className="text-sm text-slate-500">of {c.target_amount.toLocaleString()}</span>
                  </div>
                  <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-emerald-900/10">
                    <div className="h-full rounded-full bg-gradient-to-r from-emerald-700 to-gold" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="mt-1.5 text-xs font-medium text-slate-500">{pct}% towards target</div>
                </div>
              )}

              {canSupport ? (
                <button onClick={() => setOpen(true)} data-testid="support-initiative-btn" className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-emerald-900 px-6 py-3.5 text-sm font-bold text-white transition hover:bg-emerald-800">
                  {t("cta_support_this")} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </button>
              ) : (
                <div className="mt-4">
                  <p className="text-sm leading-relaxed text-slate-600">This is a coordinated CSR program. To get involved or partner with us on this initiative, please reach out.</p>
                  <Link to="/contact" className="mt-5 flex w-full items-center justify-center gap-2 rounded-full border border-emerald-900/20 px-6 py-3.5 text-sm font-bold text-emerald-900 transition hover:bg-emerald-50">{t("cta_get_involved")}</Link>
                </div>
              )}

              <div className="mt-6 space-y-2 border-t border-emerald-900/10 pt-5 text-xs text-slate-500">
                <div className="flex items-center gap-2"><Target className="h-3.5 w-3.5 text-gold" /> Status: <b className="text-emerald-900">{c.status}</b></div>
                <div className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Compliance: <b className="text-emerald-900">{c.compliance_status}</b></div>
              </div>

              <div className="mt-5 border-t border-emerald-900/10 pt-5">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gold"><Share2 className="h-3.5 w-3.5" /> Share this initiative</div>
                <div className="mt-3 flex gap-2">
                  {(() => {
                    const url = typeof window !== "undefined" ? window.location.href : "";
                    const share = [
                      { Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, label: "Facebook" },
                      { Icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, label: "LinkedIn" },
                      { Icon: MessageCircle, href: `https://wa.me/?text=${encodeURIComponent(c.title + " — " + url)}`, label: "WhatsApp" },
                    ];
                    return (
                      <>
                        {share.map((s) => (
                          <a key={s.label} href={s.href} target="_blank" rel="noreferrer" data-testid={`share-${s.label.toLowerCase()}`} aria-label={`Share on ${s.label}`} className="flex h-9 w-9 items-center justify-center rounded-full border border-emerald-900/15 text-emerald-900 transition hover:border-gold hover:bg-emerald-50"><s.Icon className="h-4 w-4" /></a>
                        ))}
                        <button onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copied"); }} data-testid="share-copy" aria-label="Copy link" className="flex h-9 w-9 items-center justify-center rounded-full border border-emerald-900/15 text-emerald-900 transition hover:border-gold hover:bg-emerald-50"><Link2 className="h-4 w-4" /></button>
                      </>
                    );
                  })()}
                </div>
                <p className="mt-2 text-[11px] leading-relaxed text-slate-400">Promotions on Instagram or Facebook can link directly here — this initiative has its own shareable page.</p>
              </div>
            </div>
          </aside>
        </Container>
      </section>

      {canSupport && <SupportCheckout open={open} onOpenChange={setOpen} campaign={c} />}
    </div>
  );
}
